import React, { useState, useEffect, useRef, useMemo } from 'react'
import { concatenateProjectVideos } from '../../../shared/services/api'
import './FinalPreview.css'
import logger from '../../../shared/utils/logger'

/**
 * 최종 영상 미리보기 컴포넌트
 * 서버에서 생성된 최종 영상을 미리보기로 표시
 */
function FinalPreview({ sections, isOpen, onClose, projectId, title, subtitleSettings, titleSettings, videoSettings }) {
  const [isLoading, setIsLoading] = useState(false)
  const [loadProgress, setLoadProgress] = useState(0)
  const [videoUrl, setVideoUrl] = useState(null)
  const [error, setError] = useState(null)
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMinimized, setIsMinimized] = useState(false) // 최소화 상태
  const [isFullscreen, setIsFullscreen] = useState(false) // 전체화면 상태

  // 영상 생성 및 로드
  useEffect(() => {
    if (!isOpen || !sections || sections.length === 0) {
      return
    }
    
    // 기존 비디오 URL 정리
    if (videoUrl && videoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrl)
      setVideoUrl(null)
    }

    const generateVideo = async () => {
      setIsLoading(true)
      setError(null)
      setLoadProgress(0)
      
      // 실시간 진행률 업데이트 핸들러
      const handleProgress = ({ progress, message }) => {
        setLoadProgress(progress)
        
        // 메시지 업데이트
        const statusEl = document.getElementById('loading-status')
        if (statusEl && message) {
          statusEl.textContent = message
        }
        
        logger.info(`영상 생성 진행률: ${progress}% - ${message}`)
      }
      
      try {
        logger.info('최종 영상 생성 시작...')
        logger.info('전달받은 섹션 데이터:', sections.map(s => ({
          id: s.id,
          text: s.text?.substring(0, 20),
          hasImage: !!s.imageFile,
          hasVideo: !!s.mp4File || !!s.videoFile,
          hasAudio: !!s.audioFile,
          hasAudioInVideo: s.hasAudioInVideo,
          mp4FileName: s.mp4File?.name,
          audioFileName: s.audioFile?.name || s.audioFile?.substring?.(0, 30)
        })))
        
        // 설정 준비 - props로 받은 설정 사용
        const finalSubtitleSettings = subtitleSettings || {
          fontSize: sections[0]?.fontSize || 48,
          fontFamily: sections[0]?.fontFamily || 'Noto Sans KR',
          textColor: sections[0]?.textColor || '#FFFFFF',
          strokeColor: sections[0]?.strokeColor || '#000000',
          strokeWidth: sections[0]?.strokeWidth || 3,
          textPosition: sections[0]?.textPosition || 'bottom'
        }
        
        const finalTitleSettings = titleSettings || {}
        if (title) {
          finalTitleSettings.title = title
        }
        
        const settings = {
          subtitleSettings: finalSubtitleSettings,
          videoSettings: {
            ...videoSettings,
            aspectRatio: '9:16',
            width: 1080,
            height: 1920,
            fitMode: 'contain', // 비율 유지하면서 중앙 배치
            backgroundColor: '#000000', // 검은 배경
            position: 'center' // 중앙 정렬
          },
          titleSettings: finalTitleSettings
        }
        
        // 서버에서 영상 생성 (진행률 콜백 전달)
        const result = await concatenateProjectVideos(projectId, sections, settings, handleProgress)
        
        // Blob 또는 URL 처리
        let url
        if (result instanceof Blob) {
          url = URL.createObjectURL(result)
        } else if (typeof result === 'string') {
          url = result
        } else {
          throw new Error('예상치 못한 응답 형식')
        }
        
        setVideoUrl(url)
        
        logger.info('최종 영상 생성 완료')
        
        // 로딩 화면 잠시 유지 후 숨김
        setTimeout(() => {
          setIsLoading(false)
        }, 500)
        
      } catch (err) {
        logger.error('영상 생성 실패:', err)
        
        // 인증 만료 에러 체크
        if (err.message.includes('인증이 만료되었습니다')) {
          setError('로그인이 만료되었습니다. 다시 로그인해주세요.')
        } else {
          setError(err.message || '영상 생성에 실패했습니다')
        }
        
        setIsLoading(false)
      }
    }

    generateVideo()
  }, [isOpen]) // isOpen만 감시

  // 컴포넌트 언마운트 시 클린업
  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl)
      }
    }
  }, [])

  // 비디오 메타데이터 로드
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  // 시간 업데이트
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  // 재생/일시정지 토글
  const togglePlay = () => {
    if (!videoRef.current) return
    
    if (isPlaying) {
      videoRef.current.pause()
      setIsPlaying(false)
    } else {
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  // 시간 이동 (시크)
  const handleSeek = (e) => {
    if (!videoRef.current) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = x / rect.width
    const newTime = percentage * duration
    
    videoRef.current.currentTime = newTime
    setCurrentTime(newTime)
  }

  // 다운로드
  const downloadVideo = async () => {
    if (!videoUrl) return
    
    try {
      // Blob URL인 경우 fetch로 가져와서 다운로드
      const response = await fetch(videoUrl)
      const blob = await response.blob()
      
      // 파일명 생성 (프로젝트 제목 사용)
      const fileName = title ? 
        `${title.replace(/[^a-zA-Z0-9가-힣]/g, '_')}_${new Date().toISOString().split('T')[0]}.mp4` : 
        `video_${Date.now()}.mp4`
      
      // Blob을 다운로드용 URL로 변환
      const downloadUrl = URL.createObjectURL(blob)
      
      // 다운로드 링크 생성 및 클릭
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      
      // URL 정리
      URL.revokeObjectURL(downloadUrl)
      
      logger.info(`영상 다운로드 완료: ${fileName}`)
    } catch (error) {
      logger.error('영상 다운로드 실패:', error)
      alert('영상 다운로드에 실패했습니다.')
    }
  }

  // 시간 포맷
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // 닫기
  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
    onClose()
  }

  // 콘텐츠 렌더링 함수
  const renderContent = () => {
    return (
      <>
        {/* 콘텐츠 영역 */}
        <div className="fp-content">
          {/* 로딩 오버레이 */}
          {isLoading && (
            <div className="fp-loading-overlay">
              <div className="fp-loading-spinner">
                <div className="fp-spinner"></div>
                <p id="loading-status">최종 영상 생성 중...</p>
                <div className="fp-loading-bar">
                  <div 
                    className="fp-loading-progress" 
                    style={{ width: `${loadProgress}%` }}
                  />
                </div>
                <p>{Math.round(loadProgress)}%</p>
              </div>
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="fp-error">
              <p>⚠️ {error}</p>
            </div>
          )}

          {/* 비디오 플레이어 */}
          {videoUrl && !error && (
            <>
              <div className="fp-video-wrapper">
                <video
                  ref={videoRef}
                  className="fp-video"
                  src={videoUrl}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />
              </div>

              {/* 컨트롤 */}
              <div className="fp-controls">
                <button 
                  className="fp-play-btn" 
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    // 일시정지 아이콘 (pause)
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 30.05 30.05"
                      fill="white"
                    >
                      <path d="M18.993,10.688h-7.936c-0.19,0-0.346,0.149-0.346,0.342v8.022c0,0.189,0.155,0.344,0.346,0.344
                        h7.936c0.19,0,0.344-0.154,0.344-0.344V11.03C19.336,10.838,19.183,10.688,18.993,10.688z"/>
                      <path d="M15.026,0C6.729,0,0.001,6.726,0.001,15.025S6.729,30.05,15.026,30.05
                        c8.298,0,15.023-6.726,15.023-15.025S23.324,0,15.026,0z M15.026,27.54c-6.912,0-12.516-5.604-12.516-12.515
                        c0-6.914,5.604-12.517,12.516-12.517c6.913,0,12.514,5.603,12.514,12.517C27.54,21.936,21.939,27.54,15.026,27.54z"/>
                    </svg>
                  ) : (
                    // 재생 아이콘 (play)
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 30.051 30.051"
                      fill="white"
                    >
                      <path d="M19.982,14.438l-6.24-4.536c-0.229-0.166-0.533-0.191-0.784-0.062
                        c-0.253,0.128-0.411,0.388-0.411,0.669v9.069c0,0.284,0.158,0.543,0.411,0.671
                        c0.107,0.054,0.224,0.081,0.342,0.081c0.154,0,0.31-0.049,0.442-0.146l6.24-4.532
                        c0.197-0.145,0.312-0.369,0.312-0.607C20.295,14.803,20.177,14.58,19.982,14.438z"/>
                      <path d="M15.026,0.002C6.726,0.002,0,6.728,0,15.028c0,8.297,6.726,15.021,15.026,15.021
                        c8.298,0,15.025-6.725,15.025-15.021C30.052,6.728,23.324,0.002,15.026,0.002z
                        M15.026,27.542c-6.912,0-12.516-5.601-12.516-12.514c0-6.91,5.604-12.518,12.516-12.518
                        c6.911,0,12.514,5.607,12.514,12.518C27.541,21.941,21.937,27.542,15.026,27.542z"/>
                    </svg>
                  )}
                </button>
                
                <div 
                  className="fp-timeline-bar"
                  onClick={handleSeek}
                >
                  <div 
                    className="fp-timeline-progress"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  />
                </div>
                
                <div className="fp-time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  if (!isOpen) return null

  // 전체화면 모드일 때
  if (isFullscreen) {
    return (
      <div className="fp-overlay fullscreen">
        <div className="fp-container">
          {/* 헤더 */}
          <div className="fp-header">
            <h2>📹 최종 영상 미리보기</h2>
            <div className="fp-header-buttons">
              <button 
                className="fp-fullscreen-btn" 
                onClick={() => setIsFullscreen(false)}
                title="위젯 모드로"
              >
                ⛶
              </button>
              <button className="fp-close-btn" onClick={handleClose}>✕</button>
            </div>
          </div>
          {renderContent()}
        </div>
      </div>
    )
  }

  // 위젯 모드
  return (
    <div className={`fp-widget ${isMinimized ? 'minimized' : ''}`}>
      {/* 헤더 */}
      <div className="fp-header" onClick={() => isMinimized && setIsMinimized(false)}>
        {isMinimized ? (
          <span style={{ 
            fontSize: '36px',
            color: '#ffffff',
            fontWeight: '200',
            textShadow: '0 0 10px rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>+</span>
        ) : (
          <h2>미리보기</h2>
        )}
        <div className="fp-header-buttons">
          {!isMinimized && (
            <>
              <button 
                className="fp-download-btn" 
                onClick={(e) => {
                  e.stopPropagation()
                  downloadVideo()
                }}
                disabled={!videoUrl || isLoading}
                title="저장"
              >
                저장
              </button>
              <button 
                className="fp-minimize-btn" 
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMinimized(true)
                }}
                title="최소화"
              >
                ⎯
              </button>
              <button 
                className="fp-fullscreen-btn" 
                onClick={(e) => {
                  e.stopPropagation()
                  setIsFullscreen(true)
                }}
                title="전체화면"
              >
                ⛶
              </button>
            </>
          )}
          <button 
            className="fp-close-btn" 
            onClick={(e) => {
              e.stopPropagation()
              handleClose()
            }}
          >
            ✕
          </button>
        </div>
      </div>
      {!isMinimized && renderContent()}
    </div>
  )
}

export default FinalPreview